/**
 * EquityFormTable —— 权益表「键位推导 / 列配置 / 重复校验」的逻辑层纯函数（无 React/ui/api 副作用，可独立单测）。
 *
 * 从 EquityFormTable/index.tsx 搬移而来，行为逐字节一致，仅把「依赖」改为入参：
 * - $sType（内包/外包）原是模块级全局（utils/sType.ts 的 let $sType），此处改为参数注入，默认 'inner'。
 * - detail.depth / config（inPlanning / view）由调用方注入。
 *
 * ⚠️ 已知不一致（按现有实现原样复刻，勿"修正"）：
 * - hasDuplicateEquity 在组件内以 key='right_code' 调用，而数据字段 / rowKey 实际为 'rights_code'，
 *   两者对不上（现有实现检查 'right_code'，大概率拿不到值，导致重复不被检出）。此处按现状保留，
 *   由用例标注提醒；改造时由产品/后端对齐后再调整。
 */
export type SType = 'inner' | 'outer';

/** getFormItemKey 入参 */
export interface FormItemKeyConfig {
  inPlanning?: boolean;
  view?: boolean;
  $sType?: SType;
}

export type FormItemKey =
  | 'expect_rights'
  | 'supplier_expect_rights_list'
  | 'expect_rights_diff_list';

/**
 * 推导权益表当前应使用的 formItemKey（自 EquityFormTable/index.tsx:47-61 搬移）。
 *
 * 分支顺序（优先级从高到低）：
 * 1. outer 或 inPlanning → 'expect_rights'
 * 2. inner 且 depth===2 → 'supplier_expect_rights_list'
 * 3. inner 且 depth===1 且 view → 'expect_rights_diff_list'
 * 4. 其余 → 'expect_rights'
 */
export function getFormItemKey(
  detail: { depth?: number } | undefined,
  config: FormItemKeyConfig = {},
): FormItemKey {
  const { inPlanning = false, view = false, $sType = 'inner' } = config;
  if ($sType === 'outer' || inPlanning) {
    return 'expect_rights';
  }
  if ($sType === 'inner' && detail?.depth === 2) {
    return 'supplier_expect_rights_list';
  }
  if ($sType === 'inner' && detail?.depth === 1 && view) {
    return 'expect_rights_diff_list';
  }
  return 'expect_rights';
}

/** computeEquityColConfig 入参 */
export interface EquityColConfigInput {
  inPlanning?: boolean;
  view?: boolean;
  depth?: number;
  $sType?: SType;
}

/** computeEquityColConfig 返回：仅 content 列返回 hideInTable，其余列返回 {} */
export interface EquityColConfigResult {
  hideInTable?: boolean;
}

/**
 * 计算权益表某列的展示配置（自 EquityFormTable/index.tsx:120-137 的 columnsConfig 搬移）。
 *
 * content 列规则：
 * - 默认 hideInTable=true；
 * - inner 且 (!view 或 inPlanning) → hideInTable=false；
 * - depth===2 且 !inPlanning → 强制 hideInTable=true（覆盖上一条）。
 * 其余列恒返回 {}。
 */
export function computeEquityColConfig(
  key: string,
  config: EquityColConfigInput = {},
): EquityColConfigResult {
  const { inPlanning = false, view = false, depth, $sType = 'inner' } = config;
  if (key !== 'content') {
    return {};
  }
  let hideInTable = true;
  if (($sType === 'inner' && !view) || ($sType === 'inner' && inPlanning)) {
    hideInTable = false;
  }
  if (depth === 2 && !inPlanning) {
    hideInTable = true;
  }
  return { hideInTable };
}

/**
 * 权益重复校验（自 EquityFormTable/index.tsx:13-34 的 hasDuplicate 搬移）。
 *
 * 空值（null / undefined / trim 后为空）跳过；同 key 的值 trim 后重复 → true。
 * ⚠️ 沿用现有实现：v 若非字符串（如 number），v.trim() 会抛错；组件内仅对字符串字段调用。
 */
export function hasDuplicateEquity(arr: any[] | null | undefined, key: string): boolean {
  const seen = new Set();
  if (!arr?.length) {
    return false;
  }
  for (const item of arr) {
    const v = item[key];
    if (v === null || v === undefined || v.trim() === '') {
      continue;
    }
    if (seen.has(v.trim())) {
      return true;
    }
    seen.add(v.trim());
  }
  return false;
}
